import {
  Button,
  MultiselectFormField,
  SearchableSelectFormField,
  SelectFormField,
  Text,
} from "@/components/ui";
import { useUserDirectory } from "@/hooks/use-user-directory";
import type { MatterRole } from "@/lib/types";
import { formatUsersName } from "@/lib/utils/format";
import type { UseFormRegisterReturn } from "react-hook-form";

export type TaskAssignmentSource = "role" | "user";

export function TaskAssignmentFields({
  label,
  roles,
  source,
  onSourceChange,
  enabled,
  selectedUserIds,
  backupUserId,
  roleRegister,
  usersRegister,
  backupRegister,
  readOnly = false,
}: {
  label: "Assignee" | "Assigner";
  readOnly?: boolean;
  roles: MatterRole[];
  source: TaskAssignmentSource;
  onSourceChange: (source: TaskAssignmentSource) => void;
  enabled: boolean;
  selectedUserIds: string[];
  backupUserId?: string | null;
  roleRegister: UseFormRegisterReturn;
  usersRegister: UseFormRegisterReturn;
  backupRegister: UseFormRegisterReturn;
}) {
  const directory = useUserDirectory({
    enabled,
    prioritizeSelectedUsers: false,
    selectedIds: selectedUserIds,
  });
  const backupDirectory = useUserDirectory({
    enabled,
    selectedIds: [backupUserId ?? ""],
  });
  return (
    <div className="border-border bg-muted/30 space-y-3 rounded-lg border p-3">
      <div className="flex items-center justify-between gap-3">
        <Text variant="body" className="font-medium">
          {label}
        </Text>
        <div
          className="bg-background grid grid-cols-2 rounded-md border p-0.5"
          role="group"
          aria-label={`${label} type`}
        >
          {(["role", "user"] as const).map((value) => (
            <Button
              key={value}
              type="button"
              size="sm"
              variant={source === value ? "default" : "ghost"}
              className="h-7 rounded-sm px-3"
              aria-pressed={source === value}
              onClick={() => {
                onSourceChange(value);
              }}
            >
              {value === "role" ? "Role" : "User"}
            </Button>
          ))}
        </div>
      </div>
      {source === "role" ? (
        <>
          <SelectFormField
            label="Role"
            items={roles.map((role) => ({ label: role.label, value: role.id }))}
            itemsKey="value"
            formatItemLabel={(item) => item?.label}
            placeholder={`Select ${label.toLowerCase()} role`}
            register={roleRegister}
          />
          <SearchableSelectFormField
            clearable
            label="Backup user"
            items={backupDirectory.users}
            itemsKey="id"
            formatItemLabel={(user) => formatUsersName(user) ?? user?.id}
            formatSelectedItemLabel={(user) =>
              formatUsersName(user) ?? user?.id
            }
            {...backupDirectory.selectProps}
            placeholder="Select a backup user"
            register={backupRegister}
          />
          <Text variant="caption" className="text-muted-foreground">
            If the {label.toLowerCase()} role is not present on the case, assign{" "}
            {label === "Assignee" ? "to" : "from"} this user.
          </Text>
        </>
      ) : (
        <MultiselectFormField
          label="Users"
          options={directory.users.map((user) => ({
            label: formatUsersName(user) ?? user.id,
            value: user.id,
          }))}
          {...directory.selectProps}
          placeholder={`Select ${label === "Assignee" ? "assignees" : "assigners"}`}
          register={usersRegister}
          showFirstSelectionAndCount
        />
      )}
      {readOnly && source === "user" && selectedUserIds.length > 0 ? (
        <Text variant="caption" className="break-words">
          {selectedUserIds
            .map((id) => {
              const user = directory.users.find((item) => item.id === id);
              return user ? formatUsersName(user) : "Unavailable user";
            })
            .join(", ")}
        </Text>
      ) : null}
    </div>
  );
}
